import { INACCURACY } from 'constants/params';
import { ClipConfig, PanZoomRotateParams } from 'services/clip.service';
import { RootState } from 'store/store';
import { useAppStore } from 'store/store-hooks';
import { useAppDispatch } from 'store/store-hooks';
import { handleClipConfig } from 'components/app/editor/_mediator/editor.slice';

import { PAN_ZOOM_BE, PAN_ZOOM_FE } from '../_pan-zoom.params';
import { PanZoomRotate, usePanZoomContext } from '../_pan-zoom.provider';
import { convertPosition, rotatePos } from './pan-zoom.helper';

// TODO: use-pan-zoom-converter.ts

export const usePanZoomFormat = () => {
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const { viewerEl, ARViewerSize, setPanZoomRotate, setARViewerSize, setZoomRect, panZoomRotateRef } =
    usePanZoomContext();

  const toClipConfig = () => {
    const { clipData, keyFrameIndex, activeClip, rotateListener } = selectData(store.getState());
    if (!viewerEl.current || !clipData) return;

    const { width, height } = ARViewerSize.current;
    const { left, bottom, zoom, rotate, aspectRatio } = panZoomRotateRef.current;
    const viewerRect = viewerEl.current.getBoundingClientRect();
    const panZoomData = clipData.config.panZoomRotate;
    const prevPayload = panZoomData[keyFrameIndex];
    const payload = isDefault(panZoomRotateRef.current)
      ? null
      : {
          positionTime: 0, // TODO: for multiple keyframes set clipData time accordingly
          moveX: convertPosition(left, 'internal', viewerRect.width, width * zoom),
          moveY: convertPosition(bottom, 'internal', viewerRect.height, height * zoom),
          zoom,
          rotate,
          aspectRatio,
        };

    if (payload === null && prevPayload === null) return;
    if (payload) {
      const position = rotatePos(payload.moveX, payload.moveY, rotateListener, 'internal');
      payload.moveX = position.moveX;
      payload.moveY = position.moveY;
    }
    if (payload && prevPayload && isUnchanged(payload, prevPayload)) return;

    const clipConfig: Partial<ClipConfig> = {
      panZoomRotate: keyFrameIndex ? [panZoomData[0], payload] : [payload, panZoomData[1]],
    };
    dispatch(
      handleClipConfig({
        data: { clipId: activeClip as ClipId, config: clipConfig },
        backupParams: { title: 'Clip Pan/Zoom' },
      })
    );
  };

  const fromClipConfig = () => {
    const { clipData, keyFrameIndex, rotateListener } = selectData(store.getState());
    if (!clipData || !viewerEl.current) return;

    const viewerRect = viewerEl.current.getBoundingClientRect();
    const { moveX, moveY, zoom, aspectRatio } = clipData.config.panZoomRotate[keyFrameIndex] ?? PAN_ZOOM_BE;

    setARViewerSize(aspectRatio, rotateListener.rotate);
    const { width, height } = ARViewerSize.current;
    const position = rotatePos(moveX, moveY, rotateListener);
    const left = convertPosition(position.moveX, 'external', viewerRect.width, width * zoom);
    const bottom = convertPosition(position.moveY, 'external', viewerRect.height, height * zoom);
    setPanZoomRotate({ left, bottom, zoom, aspectRatio });
    setZoomRect({ left, bottom, zoom });
  };

  return { toClipConfig, fromClipConfig };
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { clipsMap, panZoomKeyFrameIndex, panZoomRotateListener, activeClip } = state.editor;
  const clipData = clipsMap && activeClip && clipsMap[activeClip];
  return { clipData, keyFrameIndex: panZoomKeyFrameIndex, rotateListener: panZoomRotateListener, activeClip };
};

/* H E L P E R S */

const isDefault = (payload: PanZoomRotate) => {
  return !Object.entries(PAN_ZOOM_FE).some(([key, value]) => {
    return payload[key as keyof PanZoomRotate] !== value;
  });
};

const isUnchanged = (payload: PanZoomRotateParams, prevPayload: PanZoomRotateParams) => {
  return !Object.entries(payload).some(([key, value]) => {
    const diff = Math.abs(prevPayload[key as keyof PanZoomRotateParams] - value);
    return diff > INACCURACY;
  });
};
